"use client"

import React, { useMemo } from "react"
import { graphql, useFragment } from "@/gql"
import { PortfolioStaticDataQuery, PortfolioStaticFiltersQuery } from "@/gql/graphql"
import { Spinner, Text } from "@/elements"
import { FilterItem } from "@/components/Filters/FilterItem"
import { useFilters } from "@/components/Filters/useFilters"
import { FilterWrapper } from "@/components/Filters/FilterWrapper"

const ProjectFiltersDesktopFragment = graphql(`
  fragment ProjectFiltersDesktop on CreativeWorkPortfolioProjectConnection {
    typeCounts {
      count
      category {
        id
        name
      }
    }
    roleCounts {
      count
      category {
        id
        name
      }
    }
  }
`)

interface Props {
  staticData: PortfolioStaticDataQuery
  filtersData: PortfolioStaticFiltersQuery | null
  filtersLoading: boolean
}

export const ProjectFiltersDesktop: React.FC<Props> = ({ staticData, filtersData, filtersLoading }) => {
  const { toggleFilter, getActiveFilters, getButtonText, removeAllByIds } = useFilters()

  const filters = useFragment(
    ProjectFiltersDesktopFragment,
    filtersData?.individualCreative?.creativeWorkPortfolioProjects
  )

  const filteredTypeFilters = useMemo(() => {
    return filters?.typeCounts?.filter(f => {
      return !!f?.category?.name
    })
  }, [filters])

  const selectedRoleIds = getActiveFilters("role")
  const selectedTypeIds = getActiveFilters("type")

  const rolesButtonTextData = useMemo(() => {
    return (
      filters?.roleCounts
        ?.map(item => {
          const category = item?.category
          return category ? { id: category.id, name: category.name } : null
        })
        .filter(Boolean) ?? []
    )
  }, [filters?.roleCounts])

  const projectTypesButtonTextData = useMemo(() => {
    return (
      filteredTypeFilters
        ?.map(item => {
          const category = item?.category
          return category ? { id: category.id, name: category.name } : null
        })
        .filter(Boolean) ?? []
    )
  }, [filteredTypeFilters])

  const projectTypesButtonText = getButtonText({
    defaultText: "Project types",
    data: projectTypesButtonTextData,
    selectedIds: selectedTypeIds,
    showCount: false,
  })

  const rolesButtonText = getButtonText({
    defaultText: "Roles",
    data: rolesButtonTextData,
    selectedIds: selectedRoleIds,
    showCount: false,
  })

  const onToggleFilter = (key: string, filterId: string) => {
    toggleFilter({ params: [{ key, values: [filterId] }] })
  }

  return (
    <div className="flex flex-row items-center gap-2">
      <FilterWrapper
        id="role-filters"
        filterActive={selectedRoleIds?.length > 0}
        onRemoveAll={() => removeAllByIds(["role"])}
        hasSearch={false}
        buttonText={rolesButtonText}
        menuPosition="right">
        <div className="mt-3 flex h-full w-full flex-col gap-1">
          <div className="px-2 pb-1 pt-2">
            <Text variant="label">Roles</Text>
          </div>
          {filtersLoading ? (
            <div className="flex h-2/3 w-full items-center justify-center">
              <Spinner />
            </div>
          ) : (
            filters?.roleCounts?.map(role => {
              const category = role?.category
              if (!category) return null
              return (
                <FilterItem
                  key={category?.id}
                  name={category?.name}
                  selected={selectedRoleIds.includes(category?.id)}
                  onClick={() => onToggleFilter("role", category?.id)}
                  count={role?.count}
                />
              )
            })
          )}
        </div>
      </FilterWrapper>
      <FilterWrapper
        id="project-type-filters"
        filterActive={selectedTypeIds?.length > 0}
        onRemoveAll={() => removeAllByIds(["type"])}
        hasSearch={false}
        buttonText={projectTypesButtonText}
        menuPosition="right">
        <div className="mt-3 flex h-full w-full flex-col gap-1">
          <div className="px-2 pb-1 pt-2">
            <Text variant="label">Project types</Text>
          </div>
          {filtersLoading ? (
            <div className="flex h-2/3 w-full items-center justify-center">
              <Spinner />
            </div>
          ) : (
            filteredTypeFilters?.map(type => {
              const category = type?.category
              if (!category) return null
              return (
                <FilterItem
                  key={category?.id}
                  name={category?.name}
                  selected={selectedTypeIds.includes(category?.id)}
                  onClick={() => onToggleFilter("type", category?.id)}
                  count={type?.count}
                />
              )
            })
          )}
        </div>
      </FilterWrapper>
    </div>
  )
}
